/**
 * Client-side noise filter for the error-reporting path (DEV-5936).
 *
 * A ClickHouse audit of `default.events` (event_type = 'error') found ~80% of
 * frontend error volume is non-actionable browser/environment noise rather than
 * application bugs. This module centralises the "drop before logging" decision
 * as an allow-through filter with a maintainable denylist of matcher predicates,
 * so new noise signatures are cheap to add. It is applied in both the Sentry
 * `beforeSend` pipeline and the custom ClickHouse event sender (`logEvent`) so
 * the two sources stay consistent.
 *
 * Noise is dropped entirely (not sampled) — these events add no diagnostic value.
 */

/**
 * Normalised view of an error used by the denylist. Different sources populate
 * different fields: Sentry events expose the exception `value`/`type`, while the
 * custom event sender only has the message text.
 */
export type ErrorSignal = {
  /** Error message / value (Sentry exception value or logEvent metadata.text). */
  message: string;
  /** Error type/name when available (e.g. "AbortError", "CanceledError"). */
  type?: string;
  /** Axios-style error code when available (e.g. "ERR_CANCELED"). */
  code?: string;
};

/**
 * Real, actionable errors that must NEVER be dropped, even if a future denylist
 * entry would otherwise match them. Checked before the denylist as a safety net.
 * - "request failed with status code 500" — tracked in DEV-5935 (prod-api 500s).
 * - React #418 hydration mismatch — a real bug, fixed separately.
 */
const PRESERVE_PATTERNS: RegExp[] = [
  /request failed with status code 500/i,
  /Minified React error #418/i,
];

/**
 * Cancelled / aborted requests: user navigations or fast re-typing cancelling
 * in-flight fetches (e.g. on /search). The single largest bucket (~65%).
 */
const CANCELLED_REQUEST_TYPES = new Set(['CanceledError', 'AbortError']);
const CANCELLED_REQUEST_PATTERNS: RegExp[] = [
  /^network error$/i,
  /^canceled$/i,
];

/** Autoplay & media element noise (interrupted/unsupported playback). */
const MEDIA_NOISE_TYPES = new Set(['NotSupportedError', 'NotAllowedError']);
const MEDIA_NOISE_PATTERNS: RegExp[] = [
  /The play\(\) request was interrupted/i,
  /Failed to load because no supported source was found/i,
  /The element has no supported sources/i,
  /The operation is not supported/i,
  /play\(\) failed because the user didn't interact with the document first/i,
  /The media resource indicated by the src attribute/i,
];

/** Google Tag Manager fetch blocked by adblockers. */
const GTM_BLOCKED_PATTERN = /gtm\.uppbeat\.io/i;

/** Texthelp Read&Write browser extension (high volume, single device). */
const TEXTHELP_PATTERNS: RegExp[] = [
  /Distributor\.getValue/i,
  /getDictionaries/i,
  /isDictateAvailable/i,
  /isPredictionAvailable/i,
  /isMathOcrAvailable/i,
];

/** Google Translate extension reparenting DOM nodes that React owns. */
const TRANSLATE_DOM_PATTERNS: RegExp[] = [
  /Failed to execute 'removeChild' on 'Node'/i,
  /Failed to execute 'insertBefore' on 'Node'/i,
];

type NoiseMatcher = {
  id: string;
  test: (signal: ErrorSignal) => boolean;
};

/**
 * The denylist. Each matcher targets one category of non-actionable noise.
 * Add new signatures here rather than scattering `if` checks across the codebase.
 */
const NOISE_MATCHERS: NoiseMatcher[] = [
  {
    id: 'cancelled-or-aborted-request',
    test: ({ message, type, code }) =>
      code === 'ERR_CANCELED' ||
      (type !== undefined && CANCELLED_REQUEST_TYPES.has(type)) ||
      CANCELLED_REQUEST_PATTERNS.some((pattern) =>
        pattern.test(message.trim())
      ),
  },
  {
    id: 'autoplay-and-media-element-noise',
    test: ({ message, type }) =>
      (type !== undefined && MEDIA_NOISE_TYPES.has(type)) ||
      MEDIA_NOISE_PATTERNS.some((pattern) => pattern.test(message)),
  },
  {
    id: 'gtm-blocked-by-adblocker',
    test: ({ message }) => GTM_BLOCKED_PATTERN.test(message),
  },
  {
    id: 'texthelp-read-and-write-extension',
    test: ({ message }) =>
      TEXTHELP_PATTERNS.some((pattern) => pattern.test(message)),
  },
  {
    id: 'google-translate-dom-mutation',
    test: ({ message }) =>
      TRANSLATE_DOM_PATTERNS.some((pattern) => pattern.test(message)),
  },
];

/**
 * Returns true when an error is non-actionable noise and should be dropped
 * before logging. Defaults to keeping the error (allow-through); only a denylist
 * match drops it, and the preserve list always wins.
 */
export const shouldDropErrorEvent = (signal: ErrorSignal): boolean => {
  const message = signal.message ?? '';

  if (PRESERVE_PATTERNS.some((pattern) => pattern.test(message))) {
    return false;
  }

  return NOISE_MATCHERS.some((matcher) => matcher.test(signal));
};

/**
 * Stack-frame URLs we accept errors from — our own JS, on any uppbeat.io host.
 *
 * Sentry's `allowUrls` runs before `beforeSend`, so a miss silences Sentry
 * *and* the ClickHouse error log. The old pattern needed a subdomain, so it
 * went blind when ASSET_PREFIX was emptied and chunks moved to the apex
 * domain — only frame-less `captureMessage` calls kept reporting (DEV-6882).
 */
export const UPPBEAT_SCRIPT_URL = /^https?:\/\/([\w-]+\.)*uppbeat\.io\//;
